import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import useGetMeetings from "../../hooks/useGetMeetings";
import BackBtn from "../../widgets/BackBtn";

const Reports = () => {
  const { data, isLoading, isError, error } = useGetMeetings();
  const [searchText, setSearchText] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter meetings based on search and date
  const filteredData = (data || []).filter((meeting) => {
    const matchesSearch = [
      meeting.targetPerson.name,
      meeting.meeter.name,
      meeting.organisation.companyName,
      meeting.targetPerson.email,
      meeting.meeter.email,
    ].some((value) => value.toLowerCase().includes(searchText.toLowerCase()));

    const meetingDate = new Date(meeting.meetingDate);
    const matchesDate = selectedDate
      ? meetingDate.toDateString() === selectedDate.toDateString()
      : true;

    return matchesSearch && matchesDate;
  });

  // Handle Excel export
  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.map((meeting) => ({
        "Target Person": meeting.targetPerson.name,
        Visitor: meeting.meeter.name,
        Company: meeting.organisation.companyName,
        "Meeting Date": format(new Date(meeting.meetingDate), "dd/MM/yyyy"),
        "Start Time": format(new Date(meeting.timeFrom), "HH:mm"),
        "End Time": format(new Date(meeting.timeTo), "HH:mm"),
        Status: meeting.status,
        "Entry Time": meeting.entryTimeStamp
          ? format(new Date(meeting.entryTimeStamp), "dd/MM/yyyy HH:mm")
          : "-",
        "Exit Time": meeting.exitTimeStamp
          ? format(new Date(meeting.exitTimeStamp), "dd/MM/yyyy HH:mm")
          : "-",
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Meetings Report");
    XLSX.writeFile(workbook, "meetings_report.xlsx");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">{error.message}</Alert>;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <BackBtn />
        <Stack
          direction="row"
          gap={2}
          mb={3}
          flexWrap="wrap"
          alignItems="center"
        >
          <DatePicker
            label="Filter by Date"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            slotProps={{ textField: { variant: "outlined" } }}
          />

          <TextField
            label="Search"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: 300 }}
          />

          <Button
            variant="contained"
            onClick={handleExport}
            disabled={filteredData.length === 0}
          >
            Export to Excel
          </Button>
        </Stack>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="meetings table">
            <TableHead>
              <TableRow>
                <TableCell>Target Person</TableCell>
                <TableCell>Visitor</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Meeting Date</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Entry Time</TableCell>
                <TableCell>Exit Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((meeting) => (
                  <TableRow key={meeting._id}>
                    <TableCell>{meeting.targetPerson.name}</TableCell>
                    <TableCell>{meeting.meeter.name}</TableCell>
                    <TableCell>{meeting.organisation.companyName}</TableCell>
                    <TableCell>
                      {format(new Date(meeting.meetingDate), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell>
                      {format(new Date(meeting.timeFrom), "HH:mm")}
                    </TableCell>
                    <TableCell>
                      {format(new Date(meeting.timeTo), "HH:mm")}
                    </TableCell>
                    <TableCell>{meeting.status}</TableCell>
                    <TableCell>
                      {meeting.entryTimeStamp
                        ? format(
                            new Date(meeting.entryTimeStamp),
                            "dd/MM/yyyy HH:mm"
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {meeting.exitTimeStamp
                        ? format(
                            new Date(meeting.exitTimeStamp),
                            "dd/MM/yyyy HH:mm"
                          )
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
